import { uniqBy } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { createMockableHook } from '../../utils/createMockableHook';
import { FILTER_OPTIONS_DIVIDER } from './constants';
import { addDeletedItemsToList } from './deletedItemUtils';
import type {
	FilterDropdownConfigList,
	FilterItem,
	FilterOptionType,
	FilterValue,
} from './types';
import { getSubValueAsArray, getValueAsArray } from './utils';

interface UseFilterItemsProps {
	getItems?: FilterDropdownConfigList['getItems'];
	getItemsById?: FilterDropdownConfigList['getItemsById'];
	searchTerm?: string;
	value?: FilterValue;
	excludeItems?: string[];
	isNested?: boolean;
	filterOptionType?: FilterOptionType;
}

const useFilterItemsInternal = ({
	getItems,
	getItemsById,
	searchTerm = '',
	value,
	excludeItems,
	isNested = false,
	filterOptionType,
}: UseFilterItemsProps) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [items, setItems] = useState<FilterItem[]>([]);

	const selectedValues = useMemo(
		() => (isNested ? getSubValueAsArray(value) : getValueAsArray(value)),
		[value, isNested]
	);

	useEffect(() => {
		if (!getItems) {
			return;
		}

		if (typeof getItems !== 'function') {
			const filteredItems = getItems
				.filter(
					(item) =>
						item !== FILTER_OPTIONS_DIVIDER &&
						(item.label
							.toLowerCase()
							.includes(searchTerm.toLocaleLowerCase()) ||
							item.value
								?.toString()
								?.toLowerCase()
								?.includes(searchTerm.toLocaleLowerCase()))
				)
				.filter(
					(item) =>
						item !== FILTER_OPTIONS_DIVIDER &&
						!excludeItems?.includes(
							typeof item.value === 'string'
								? item.value
								: item.label.toString()
						)
				) as FilterItem[];

			const itemsWithDeletedItems = addDeletedItemsToList(
				filteredItems,
				selectedValues,
				filterOptionType,
				getItems
			);

			setItems(itemsWithDeletedItems);
		} else {
			setLoading(true);
			setError(false);

			let isCancelled = false;

			const getAllItems = async () => {
				let allItems = await getItems(1, searchTerm);

				if (isCancelled) return null;

				if (
					selectedValues?.length > 0 &&
					selectedValues.some(
						(selectedValue) =>
							!allItems.find(
								(item) =>
									item !== FILTER_OPTIONS_DIVIDER &&
									item.value === selectedValue
							)
					)
				) {
					const itemsById = await Promise.resolve(
						getItemsById ? getItemsById(selectedValues as string[]) : []
					);

					if (isCancelled) return null;
					// filter the selected items by the search term
					const filteredItems = itemsById.filter(
						(item) =>
							item.label
								.toLowerCase()
								.includes(searchTerm.toLocaleLowerCase()) ||
							item.value
								?.toString()
								?.toLowerCase()
								?.includes(searchTerm.toLocaleLowerCase())
					);

					// combine with other items and remove duplicates
					allItems = uniqBy(
						[...filteredItems, ...allItems],
						(item) => item !== FILTER_OPTIONS_DIVIDER && item.value
					);
				}

				let unfilteredItems = allItems;
				if (searchTerm && selectedValues?.length > 0) {
					// fetch existing selected items by id if it exists
					// otherwise, use the getItems function with no search term
					if (getItemsById) {
						const itemsByIdResult = await getItemsById(
							selectedValues as string[]
						);
						if (isCancelled) return null;
						unfilteredItems = itemsByIdResult;
					} else {
						unfilteredItems = await getItems(1, '');
						if (isCancelled) return null;
					}
				}

				const finalItems = allItems.filter(
					(item) =>
						item !== FILTER_OPTIONS_DIVIDER &&
						!excludeItems?.includes(
							typeof item.value === 'string'
								? item.value
								: item.label.toString()
						)
				) as FilterItem[];

				return addDeletedItemsToList(
					finalItems,
					selectedValues,
					filterOptionType,
					unfilteredItems
				);
			};

			getAllItems()
				.then((result) => {
					if (!isCancelled && result) {
						setItems(result);
					}
				})
				.catch(() => {
					if (!isCancelled) {
						setError(true);
					}
				})
				.finally(() => {
					if (!isCancelled) {
						setLoading(false);
					}
				});

			return () => {
				isCancelled = true;
			};
		}
	}, [
		getItems,
		searchTerm,
		getItemsById,
		selectedValues,
		excludeItems,
		filterOptionType,
	]);

	return {
		loading,
		error,
		items,
	};
};

export const [useFilterItems, MockUseFilterItemsProvider] = createMockableHook(
	useFilterItemsInternal
);
